import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CouponBanner from './CouponBanner';
import { resolveCategoryMeta } from './categoryDirectoryConfig';
import CommunityShowcase from "./CommunityShowcase";
import "./CommunityShowcase.css";
import './VisitResturants.css';
import './DirectorySearch.css';
import './BusinessSearchDeepLink.css';
import BusinessActionRow from "./BusinessActionRow";
import BusinessPhotoGallery, {
  normalizeBusinessPhotos,
} from "./BusinessPhotoGallery";
import ComingSoonDirectory from "./ComingSoonDirectory";
import useManagedCategories from "./useManagedCategories";
const configuredApiBase =
  typeof process !== 'undefined' && process.env
    ? process.env.REACT_APP_GOOURHUB_BUSINESS_API
    : '';

const API_BASE_URL = (
  configuredApiBase || 'https://business-api.goourhub.com'
).replace(/\/+$/, '');

const PAGE_LIMIT = 200;
const MAX_TOTAL_PAGES = 100;
const MAX_TILT = 8;

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return '';
};

const parseDetails = (business) => {
  if (
    business?.details &&
    typeof business.details === 'object' &&
    !Array.isArray(business.details)
  ) {
    return business.details;
  }

  if (
    business?.details_json &&
    typeof business.details_json === 'object' &&
    !Array.isArray(business.details_json)
  ) {
    return business.details_json;
  }

  if (typeof business?.details_json === 'string') {
    try {
      const parsed = JSON.parse(business.details_json);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      console.warn('Unable to parse business details_json.', error);
    }
  }

  return {};
};

const buildLocationAddress = (details) => {
  if (!Array.isArray(details?.locations) || details.locations.length === 0) {
    return '';
  }

  const location = details.locations[0];

  if (typeof location === 'string') {
    return location.trim();
  }

  if (!location || typeof location !== 'object') {
    return '';
  }

  const directAddress = firstText(
    location.full_address,
    location.fullAddress,
    location.address
  );

  if (directAddress) {
    return directAddress;
  }

  return [
    firstText(location.street_address, location.streetAddress, location.street),
    location.city,
    location.state,
    firstText(location.zip_code, location.zipCode, location.zip),
  ]
    .filter(Boolean)
    .join(', ');
};

const normalizeExternalUrl = (value) => {
  const cleanValue = firstText(value);

  if (!cleanValue) {
    return '';
  }

  if (/^https?:\/\//i.test(cleanValue)) {
    return cleanValue;
  }

  return `https://${cleanValue}`;
};

const normalizeBusiness = (business, fallbackIcon) => {
  const details = parseDetails(business);
  const photoData = normalizeBusinessPhotos(business, details);
  
  const profilePath = firstText(
    business?.profile_path,
    business?.profilePath,
    details.profile_path,
    details.profilePath,
    details.legacy_profile
  );
  const website = normalizeExternalUrl(
    firstText(
      business?.website,
      business?.url,
      details.website,
      details.url
    )
  );
  const source = firstText(business?.source, details.source).toLowerCase();
  const listingPlan = firstText(
    business?.listing_plan,
    business?.listingPlan,
    details.listing_plan,
    details.listingPlan
  ).toLowerCase();
  const paymentStatus = firstText(
    business?.payment_status,
    business?.paymentStatus,
    details.payment_status,
    details.paymentStatus
  ).toLowerCase();

  const isPremium =
    ['premium', 'featured', 'paid'].includes(listingPlan) ||
    ['paid', 'complete', 'completed', 'succeeded'].includes(paymentStatus) ||
    Boolean(details.legacy_premium_label);

  const isOwnerSubmitted =
    Boolean(business?.email_verified_at || business?.emailVerifiedAt) ||
    source.includes('partner') ||
    source.includes('add business') ||
    source.includes('owner');

  return {
    ...business,
    id: firstText(business?.id, details.canonical_id) || business?.name,
    name: firstText(business?.name, details.name) || 'Business',
    icon: firstText(business?.icon, details.icon) || fallbackIcon || '🏪',
    address: firstText(
      business?.address,
      business?.full_address,
      business?.fullAddress,
      details.address,
      details.full_address,
      details.fullAddress,
      buildLocationAddress(details)
    ),
    phone: firstText(
      business?.phone_number,
      business?.phoneNumber,
      business?.phone,
      details.phone_number,
      details.phoneNumber,
      details.phone
    ),
    website,
    mapUrl: firstText(
      business?.map_url,
      business?.mapUrl,
      details.map_url,
      details.mapUrl
    ),
    photos: photoData.photos,
coverPhoto: photoData.coverPhoto,
    profilePath,
    isPremium,
    isOwnerSubmitted,
  };
};

const buildMapUrl = (item) =>
  item.mapUrl ||
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    item.address || item.name || ''
  )}`;

const cleanSearchValue = (value) => {
  if (Array.isArray(value)) {
    return value.map(cleanSearchValue).filter(Boolean).join(' ');
  }

  if (value && typeof value === 'object') {
    return Object.values(value).map(cleanSearchValue).filter(Boolean).join(' ');
  }

  return typeof value === 'string' || typeof value === 'number'
    ? String(value)
    : '';
};

const matchesBusinessSearch = (business, rawQuery) => {
  const query = String(rawQuery || '').trim().toLowerCase();

  if (!query) {
    return true;
  }

  const details = parseDetails(business);
  const searchText = [
    business?.name,
    business?.address,
    business?.phone,
    business?.phone_number,
    business?.category,
    business?.website,
    details?.specialties,
    details?.source_categories,
    details?.category_tags,
    details?.communities,
    details?.languages,
    details?.locations,
  ]
    .map(cleanSearchValue)
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return searchText.includes(query);
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const fetchCategoryPage = async (categoryValue, page, signal) => {
  const requestUrl =
    `${API_BASE_URL}/api/businesses` +
    `?category=${encodeURIComponent(categoryValue)}` +
    `&page=${page}` +
    `&limit=${PAGE_LIMIT}` +
    '&sort=name';

  const response = await fetch(requestUrl, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });

  let payload;

  try {
    payload = await response.json();
  } catch {
    throw new Error('The business directory returned an invalid response.');
  }

  if (!response.ok || payload?.success === false) {
    throw new Error(
      payload?.message || `Unable to load businesses (${response.status}).`
    );
  }

  if (!Array.isArray(payload?.businesses)) {
    throw new Error('The business directory did not return a business list.');
  }

  return payload;
};

function BusinessCategoryPage({
  categoryValue = '',
  title = '',
  icon = '',
  showCouponBanner = true,
}) {
  const { categorySlug = '' } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const targetBusinessId = searchParams.get('business') || '';
  const requestedCategory = categoryValue || categorySlug;
  const {
  categories: managedCategories,
} = useManagedCategories(API_BASE_URL);

const categoryMeta = useMemo(() => {
  const resolved = resolveCategoryMeta(requestedCategory);

  const requested = String(requestedCategory || "")
    .trim()
    .toLowerCase();

  const managed = managedCategories.find(
    (item) =>
      String(item.slug || "").toLowerCase() === requested ||
      item.value === requested ||
      item.value === resolved.value
  );

  return {
    ...resolved,
    value: managed?.value || resolved.value,
    slug: managed?.slug || resolved.slug,
    label: title || managed?.name || resolved.label,
    icon: icon || managed?.icon || resolved.icon,
  };
}, [
  requestedCategory,
  title,
  icon,
  managedCategories,
]);

  const [businesses, setBusinesses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [highlightedBusinessId, setHighlightedBusinessId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [reloadNumber, setReloadNumber] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    const loadBusinesses = async () => {
      setLoading(true);
      setErrorMessage('');
      setBusinesses([]);
      setSelected(null);
      setHighlightedBusinessId('');
      setSearchQuery('');

      try {
        const firstPage = await fetchCategoryPage(
          categoryMeta.value,
          1,
          controller.signal
        );

        const reportedTotalPages =
          Number(firstPage?.pagination?.totalPages) || 1;
        const totalPages = Math.min(
          MAX_TOTAL_PAGES,
          Math.max(1, reportedTotalPages)
        );

        const allBusinesses = [...firstPage.businesses];

        for (let page = 2; page <= totalPages; page += 1) {
          const pagePayload = await fetchCategoryPage(
            categoryMeta.value,
            page,
            controller.signal
          );
          allBusinesses.push(...pagePayload.businesses);
        }

        const uniqueBusinesses = new Map();

        allBusinesses.forEach((business, index) => {
          const fallbackKey = [
            business?.name,
            business?.address,
            business?.phone_number,
            index,
          ].join('|');

          uniqueBusinesses.set(business?.id || fallbackKey, business);
        });

        const normalizedBusinesses = Array.from(uniqueBusinesses.values())
          .map((business) => normalizeBusiness(business, categoryMeta.icon))
          .sort((left, right) =>
            left.name.localeCompare(right.name, undefined, {
              sensitivity: 'base',
            })
          );

        if (!controller.signal.aborted) {
          setBusinesses(normalizedBusinesses);
        }
      } catch (error) {
        if (error.name !== 'AbortError' && !controller.signal.aborted) {
          console.error(
            `Unable to load ${categoryMeta.value} businesses.`,
            error
          );
          setErrorMessage(
            error.message || 'Unable to load businesses. Please try again.'
          );
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadBusinesses();

    return () => controller.abort();
  }, [categoryMeta.value, categoryMeta.icon, reloadNumber]);

  const focusBusiness = (business) => {
    if (!business) {
      return undefined;
    }

    setSearchQuery('');
    setHighlightedBusinessId(String(business.id));

    const scrollTimer = window.setTimeout(() => {
      const targetElement = Array.from(
        document.querySelectorAll('[data-business-id]')
      ).find(
        (element) =>
          String(element.getAttribute('data-business-id')) ===
          String(business.id)
      );

      targetElement?.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'center',
      });

      if (typeof targetElement?.focus === 'function') {
        try {
          targetElement.focus({ preventScroll: true });
        } catch {
          targetElement.focus();
        }
      }
    }, 100);

    const modalTimer = window.setTimeout(
      () => setSelected(business),
      prefersReducedMotion() ? 120 : 650
    );

    const highlightTimer = window.setTimeout(
      () => setHighlightedBusinessId(''),
      4200
    );

    return () => {
      window.clearTimeout(scrollTimer);
      window.clearTimeout(modalTimer);
      window.clearTimeout(highlightTimer);
    };
  };

  useEffect(() => {
    if (!targetBusinessId || loading || businesses.length === 0) {
      return undefined;
    }

    const targetBusiness = businesses.find(
      (business) => String(business.id) === String(targetBusinessId)
    );

    if (!targetBusiness) {
      return undefined;
    }

    return focusBusiness(targetBusiness);
    // focusBusiness intentionally uses the current rendered business list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetBusinessId, loading, businesses]);

  const filteredBusinesses = useMemo(
    () =>
      businesses.filter((business) =>
        matchesBusinessSearch(business, searchQuery)
      ),
    [businesses, searchQuery]
  );

  const clearSearch = () => setSearchQuery('');

  const handleTileClick = (item) => {
    if (item.profilePath?.startsWith('/')) {
      navigate(item.profilePath);
      return;
    }

    setSelected(item);
  };

  const closePopup = () => {
    setSelected(null);

    if (searchParams.has('business')) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('business');
      setSearchParams(nextParams, { replace: true });
    }
  };
  const dialogTitleId = `business-popup-${categoryMeta.slug}`;

  return (
    <div className="categories-container">
      {showCouponBanner && <CouponBanner />}

      {!loading && !errorMessage && businesses.length > 0 && (
        <section
          className="directory-search-panel"
          aria-label={`${categoryMeta.label} business search`}
        >
          <label className="directory-search__label" htmlFor="category-business-search">
            Search {categoryMeta.label}
          </label>

          <div className="directory-search__control">
            <input
              id="category-business-search"
              type="search"
              className="directory-search__input"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search business name, address, specialty, community, or language..."
              autoComplete="off"
            />

            {searchQuery && (
              <button
                type="button"
                className="directory-search__clear"
                onClick={clearSearch}
              >
                Clear
              </button>
            )}
          </div>

          <p className="directory-search__status" aria-live="polite">
            {searchQuery
              ? `${filteredBusinesses.length} of ${businesses.length} businesses matched.`
              : `${businesses.length} available businesses in this category.`}
          </p>

          {searchQuery && filteredBusinesses.length > 0 && (
            <div
              className="business-search-suggestions"
              aria-label="Matching businesses"
            >
              <span className="business-search-suggestions__label">
                Matching businesses
              </span>

              <div className="business-search-suggestions__list">
                {filteredBusinesses.slice(0, 8).map((business) => (
                  <button
                    key={business.id}
                    type="button"
                    className="business-search-suggestion"
                    onClick={() => focusBusiness(business)}
                  >
                    <strong>{business.name}</strong>
                    <span>
                      {categoryMeta.icon} {categoryMeta.label}
                      {business.address ? ` • ${business.address}` : ''}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {loading && (
        <div className="category-section">
          <h2 className="category-header">{categoryMeta.label}</h2>
          <p role="status">Loading businesses...</p>
        </div>
      )}

      {!loading && errorMessage && (
        <div className="category-section">
          <h2 className="category-header">{categoryMeta.label}</h2>
          <p role="alert">{errorMessage}</p>
          <button
            type="button"
            onClick={() => setReloadNumber((value) => value + 1)}
          >
            Try Again
          </button>
        </div>
      )}

    {!loading &&
  !errorMessage &&
  businesses.length === 0 && (
    <section className="category-section">
      <h2 className="category-header">
        {categoryMeta.label}
      </h2>

      <p>
        No approved businesses are currently
        available in this category.
      </p>
    </section>
  )}

      {!loading &&
        !errorMessage &&
        businesses.length > 0 &&
        filteredBusinesses.length > 0 && (
          <CommunityShowcase
            title={categoryMeta.label}
            icon={categoryMeta.icon || "🏪"}
            businesses={filteredBusinesses}
            loading={false}
            error=""
            onSelectBusiness={handleTileClick}
            
          />
        )}

      {!loading &&
        !errorMessage &&
        businesses.length > 0 &&
        filteredBusinesses.length === 0 && (
          <section className="category-section">
            <div className="directory-search-empty">
              No businesses matched <strong>{searchQuery}</strong> in{' '}
              {categoryMeta.label}.
              <button type="button" onClick={clearSearch}>
                Show all businesses
              </button>
            </div>
          </section>
        )}

      <div className="back-home-tile">
        <Link to="/categories" className="back-home-link">
          View All Categories
        </Link>
      </div>

      <div className="back-home-tile">
        <Link to="/" className="back-home-link">
          Go Back to Homepage
        </Link>
      </div>

      {selected && (
        <div className="popup-overlay" onClick={closePopup}>
          <div
            className="popup-box"
            role="dialog"
            aria-modal="true"
            aria-labelledby={dialogTitleId}
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id={dialogTitleId}>{selected.name}</h3>
            <BusinessPhotoGallery
  photos={selected.photos}
  coverPhoto={selected.coverPhoto}
  businessName={selected.name}
/>

            {selected.address && (
              <p>
                <strong>Address:</strong> {selected.address}
              </p>
            )}

            {selected.phone && (
              <p>
                <strong>Phone:</strong> {selected.phone}
              </p>
            )}

            {(selected.address || selected.mapUrl) && (
              <p>
                <a
                  href={buildMapUrl(selected)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="map-link-button"
                >
                  📍 Take Me There
                </a>
              </p>
            )}

            {selected.website && (
              <p>
                <a
                  href={selected.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="business-website-link"
                >
                  {selected.website
                    .replace(/^https?:\/\//, '')
                    .replace(/\/$/, '')}
                </a>
              </p>
            )}

            {/* Full Save / Share row for the selected business */}
            <div style={{ margin: '14px 0 8px' }}>
              <BusinessActionRow
                businessId={selected.id}
                businessName={selected.name}
              />
            </div>

            <button type="button" onClick={closePopup}>
              Close
            </button>

            {!selected.isOwnerSubmitted && (
              <>
                <hr className="popup-divider" />
                <p className="disclaimer-text">
                  Disclaimer: This listing has not been claimed or verified by
                  the business owner. Information provided is based on publicly
                  available sources.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default BusinessCategoryPage;
